import { useMemo } from 'react';
import { formatMoney, toCents } from '@monrdv/shared';

/**
 * The activity timeline.
 *
 * One ordered story of what happened to a customer or a job — jobs opened, visits booked
 * and done, quotes sent and answered, invoices issued and paid, calls with their summaries,
 * messages both ways, reviews.
 *
 * It exists so a manager can answer "what is going on with this?" in one glance instead of
 * opening five tabs and assembling the sequence themselves. That reassembly is the actual
 * work being eliminated here.
 *
 * The server builds this as a single ordered query, so the client never merges or sorts
 * anything — a client-side merge is where an event quietly ends up in the wrong month.
 */

export type TimelineEvent = {
  at: string;
  kind: string;
  title: string;
  detail: string | null;
  entity: string | null;
  entityId: string | null;
  jobId?: string | null;
  jobNumber?: string | null;
  actorName: string | null;
  amount: string | null;
  status: string | null;
};

/** Visual weight per event family. Money and customer contact are what people scan for. */
const TONE: Record<string, 'money' | 'contact' | 'work' | 'neutral'> = {
  'quote.sent': 'money',
  'quote.accepted': 'money',
  'quote.declined': 'money',
  'invoice.issued': 'money',
  'invoice.paid': 'money',
  'payment.received': 'money',
  'call.inbound': 'contact',
  'call.outbound': 'contact',
  'message.received': 'contact',
  'message.sent': 'contact',
  'review.positive': 'contact',
  'review.negative': 'contact',
  'appointment.booked': 'work',
  'appointment.arrived': 'work',
  'appointment.completed': 'work',
  'time.logged': 'work',
  'job.created': 'neutral',
  'job.stage': 'neutral',
};

export function Timeline({
  events,
  loading,
  locale = 'fr',
  emptyMessage = 'Aucune activité enregistrée.',
}: {
  events: TimelineEvent[];
  loading?: boolean;
  locale?: 'fr' | 'en';
  emptyMessage?: string;
}) {
  // Grouped by day so the eye can find "last Tuesday" without reading every timestamp.
  const days = useMemo(() => {
    const grouped = new Map<string, TimelineEvent[]>();
    for (const event of events) {
      const key = event.at.slice(0, 10);
      const list = grouped.get(key);
      if (list) list.push(event);
      else grouped.set(key, [event]);
    }
    return [...grouped.entries()];
  }, [events]);

  if (loading) return <p className="timeline__empty">Chargement…</p>;
  if (!events.length) return <p className="timeline__empty">{emptyMessage}</p>;

  return (
    <div className="timeline">
      {days.map(([day, dayEvents]) => (
        <section key={day} className="timeline__day">
          <header className="timeline__date label">{formatDay(day, locale)}</header>
          <ol className="timeline__list">
            {dayEvents.map((event, index) => (
              <li key={`${event.kind}-${event.entityId ?? index}-${event.at}`} className={`timeline__row timeline__row--${TONE[event.kind] ?? 'neutral'}`}>
                <span className="timeline__time tnum">{formatTime(event.at, locale)}</span>
                <span className="timeline__marker" aria-hidden="true" />
                <span className="timeline__body">
                  <span className="timeline__title">
                    {event.title}
                    {event.jobNumber && <span className="timeline__job">· {event.jobNumber}</span>}
                  </span>
                  {/* A call's summary lands here, which is what makes a call history
                      readable rather than a column of durations. */}
                  {event.detail && <span className="timeline__detail">{event.detail}</span>}
                  {event.actorName && <span className="timeline__actor">{event.actorName}</span>}
                </span>
                {event.amount && Number(event.amount) !== 0 && (
                  <span className="timeline__amount money">{formatMoney(toCents(event.amount), locale)}</span>
                )}
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function formatDay(isoDay: string, locale: 'fr' | 'en'): string {
  const date = new Date(`${isoDay}T12:00:00Z`);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (same(date, today)) return locale === 'fr' ? "Aujourd'hui" : 'Today';
  if (same(date, yesterday)) return locale === 'fr' ? 'Hier' : 'Yesterday';

  return date.toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    // The year only when it is not the current one: repeating it on every row is noise.
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}

function formatTime(iso: string, locale: 'fr' | 'en'): string {
  return new Date(iso).toLocaleTimeString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    hour: '2-digit',
    minute: '2-digit',
  });
}
